import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import axios from '../config/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import { useToast } from '../context/ToastContext';
import EmptyState from '../components/EmptyState';
import type { CheckoutPaymentMethod } from '../utils/paymentMethodLabels';
import { isHostedCheckoutMethod, paymentMethodFromMpgsOrder } from '../utils/mpgsWallets';
import {
  computePromotionDiscount,
  isOfferPromotion,
  isPromotionActiveNow,
} from '../utils/promotionSchedule';
import { trackFunnelEvent } from '../utils/trackFunnelEvent';
import {
  getCafeClosedMessage,
  isCafeOpenNow,
  storeHoursFromCafe,
  type StoreHoursConfig,
} from '../utils/storeHours';
import { FREE_DRINK_POINTS } from '../constants/points';
import {
  FULFILLMENT_OPTIONS,
  type FulfillmentType,
} from '../constants/fulfillment';
import DeliveryLocationPicker, {
  type CafeOrigin,
} from '../components/DeliveryLocationPicker';
import {
  deliveryFieldsFromPending,
  formatDrivingKm,
  type DeliveryDropoff,
} from '../utils/deliveryDropoff';
import { deliveryFeeJod } from '../utils/deliveryFee';
import { getCustomerTokenSync } from '../utils/authStorage';
import {
  cartPathFromCheckoutExit,
  isCheckoutCancelUrl,
  openHostedCheckoutUrl,
  writeMpgsPayLaunch,
} from '../utils/mpgsCheckout';
import { hasPendingSubscriptionCheckout } from '../utils/mpgsSubscription';

type CafePromotion = {
  id: string;
  title: string;
  description: string;
  discount_percentage?: number;
  discount_amount?: number;
  promotion_type?: string | null;
  deal_type?: string | null;
  bogo_off_percent?: number | null;
  offer_applies_to?: string | null;
  valid_from: string;
  valid_until: string;
  daily_start_time?: string | null;
  daily_end_time?: string | null;
  active?: boolean | number;
};

type PendingCheckoutOrderData = {
  cafeId: string;
  items: Array<{
    menu_item_id: string;
    quantity: number;
    note?: string;
    customizations?: Record<string, string>;
  }>;
  pointsUsed: number;
  walletUsed: number;
  orderName?: string;
  contactPhone?: string;
  promotionId?: string;
  fulfillmentType: FulfillmentType;
  paymentMethod?: CheckoutPaymentMethod | '';
  deliveryAddress?: string;
  deliveryLat?: number;
  deliveryLng?: number;
  deliveryPlaceId?: string;
};

type MpgsHostedCheckoutResponse = {
  sessionId?: string | null;
  checkoutScriptUrl?: string;
  checkoutScriptUrls?: string[];
  gatewayOrderId?: string;
  gatewayBaseUrl?: string;
  checkoutUrl?: string | null;
  checkoutUrls?: string[];
  successIndicator?: string | null;
};

declare global {
  interface Window {
    Checkout?: {
      configure: (config: Record<string, any>) => void;
      showPaymentPage: () => void;
      showEmbeddedPage?: (selector: string) => void;
    };
    qhMpgsError?: () => void;
    qhMpgsCancel?: () => void;
    qhMpgsComplete?: () => void;
  }
}

export default function Cart() {
  const { items, cafeId, updateQuantity, removeItem, clearCart, getTotal } = useCart();
  const { user, token, loginAsGuest, updateUser } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [pointsToUse, setPointsToUse] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderName, setOrderName] = useState('');
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [walletToUse, setWalletToUse] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<CheckoutPaymentMethod | ''>('');
  const [checkoutStatusMessage, setCheckoutStatusMessage] = useState('');
  const [cafePromotions, setCafePromotions] = useState<CafePromotion[]>([]);
  const [selectedPromotionId, setSelectedPromotionId] = useState('');
  const [promotionManuallySelected, setPromotionManuallySelected] = useState(false);
  const [storeHours, setStoreHours] = useState<StoreHoursConfig | null>(null);
  const [isOpenNow, setIsOpenNow] = useState(true);
  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentType>('pickup');
  const [deliveryDropoff, setDeliveryDropoff] = useState<DeliveryDropoff | null>(null);
  const [cafeOrigin, setCafeOrigin] = useState<CafeOrigin | null>(null);
  const [cafeName, setCafeName] = useState('');
  const [contactPhone, setContactPhone] = useState(user?.phone || '');
  const [redeemAllPoints, setRedeemAllPoints] = useState(false);
  const mpgsReturnHandled = useRef<string | null>(null);
  const mpgsRecoveryInFlight = useRef(false);
  const hostedSessionLaunchInFlight = useRef(false);

  const total = getTotal();

  useEffect(() => {
    if (!cafeId) {
      setCafePromotions([]);
      setSelectedPromotionId('');
      setPromotionManuallySelected(false);
      setStoreHours(null);
      setIsOpenNow(true);
      setCafeOrigin(null);
      setCafeName('');
      setDeliveryDropoff(null);
      return;
    }
    axios
      .get(`/api/cafes/${cafeId}`)
      .then((res) => {
        const hours = storeHoursFromCafe(res.data);
        setStoreHours(hours);
        setIsOpenNow(isCafeOpenNow(hours));
        const name = String(res.data?.name || '').trim();
        setCafeName(name);
        const lat = Number(res.data?.latitude);
        const lng = Number(res.data?.longitude);
        setCafeOrigin(
          Number.isFinite(lat) && Number.isFinite(lng)
            ? { lat, lng, name: name || res.data?.name }
            : null
        );
      })
      .catch(() => {
        setStoreHours(null);
        setIsOpenNow(true);
        setCafeOrigin(null);
        setCafeName('');
      });
    axios
      .get<CafePromotion[]>(`/api/promotions/cafe/${cafeId}`)
      .then((res) => {
        const list = (res.data || []).filter(
          (p) => isPromotionActiveNow(p) && isOfferPromotion(p)
        );
        setCafePromotions(list);
        setSelectedPromotionId((prev) =>
          prev && list.some((p) => p.id === prev) ? prev : ''
        );
        setPromotionManuallySelected(false);
      })
      .catch(() => setCafePromotions([]));
  }, [cafeId]);

  useEffect(() => {
    if (promotionManuallySelected) return;
    if (cafePromotions.length === 0) {
      setSelectedPromotionId('');
      return;
    }
    const best = cafePromotions.reduce<CafePromotion | null>((acc, promo) => {
      if (!acc) return promo;
      const accDiscount = computePromotionDiscount(total, acc, items);
      const nextDiscount = computePromotionDiscount(total, promo, items);
      if (nextDiscount > accDiscount) return promo;
      return acc;
    }, null);
    const bestId = best?.id || '';
    setSelectedPromotionId((prev) => (prev === bestId ? prev : bestId));
  }, [cafePromotions, total, promotionManuallySelected, items]);

  useEffect(() => {
    if (!storeHours) return;
    const refresh = () => setIsOpenNow(isCafeOpenNow(storeHours));
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [storeHours]);

  useEffect(() => {
    setContactPhone(user?.phone || '');
  }, [user?.id, user?.phone]);

  const selectedPromo =
    cafePromotions.find((p) => p.id === selectedPromotionId) || null;
  const promotionDiscount = selectedPromo
    ? computePromotionDiscount(total, selectedPromo, items)
    : 0;
  const BASE_SERVICE_FEE_PERCENT = Number(process.env.REACT_APP_SERVICE_FEE_PERCENT || 5);
  const SUBSCRIBER_SERVICE_FEE_PERCENT = Number(
    process.env.REACT_APP_SUBSCRIBER_SERVICE_FEE_PERCENT || 4
  );
  const SERVICE_FEE_MIN_JOD = Number(process.env.REACT_APP_SERVICE_FEE_MIN_JOD || 0.2);
  const SERVICE_FEE_MAX_JOD = Number(process.env.REACT_APP_SERVICE_FEE_MAX_JOD || 1.0);
  const SMALL_ORDER_THRESHOLD_JOD = Number(process.env.REACT_APP_SMALL_ORDER_THRESHOLD_JOD || 3.0);
  const SMALL_ORDER_FEE_JOD = Number(process.env.REACT_APP_SMALL_ORDER_FEE_JOD || 0.3);
  const JORDAN_TAX_PERCENT = Number(process.env.REACT_APP_JORDAN_TAX_PERCENT || 16);
  const maxFreeDrinks = Math.floor((user?.points || 0) / FREE_DRINK_POINTS);
  const maxRedeemablePoints = maxFreeDrinks * FREE_DRINK_POINTS;
  const pointsToUseForFreeDrinks = Math.min(pointsToUse, (user?.points || 0), maxFreeDrinks * FREE_DRINK_POINTS);
  const freeDrinks = Math.floor(pointsToUseForFreeDrinks / FREE_DRINK_POINTS);
  // For simplicity, we'll apply free drink discount as points value (cheapest drink price)
  const cheapestDrinkPrice = items.length > 0 ? Math.min(...items.map(i => i.price)) : 0;
  const freeDrinkDiscount = freeDrinks * cheapestDrinkPrice;
  
  const effectiveFulfillment: FulfillmentType = fulfillmentType;
  const totalAfterPromo = Math.max(0, total - promotionDiscount);
  const totalAfterDineIn = totalAfterPromo;
  const totalAfterPoints = Math.max(0, totalAfterDineIn - freeDrinkDiscount);
  const availableWallet = Number(user?.wallet_balance || 0);
  const effectiveWalletInput = walletEnabled
    ? walletToUse > 0
      ? walletToUse
      : Math.min(availableWallet, totalAfterPoints)
    : 0;
  const walletCreditUsed = Math.max(0, Math.min(effectiveWalletInput, availableWallet, totalAfterPoints));
  const serviceFeePercent = user?.subscription_active
    ? SUBSCRIBER_SERVICE_FEE_PERCENT
    : BASE_SERVICE_FEE_PERCENT;
  const serviceFeeBase = Math.max(0, totalAfterPoints);
  const rawServiceFee = serviceFeeBase * (serviceFeePercent / 100);
  const serviceFee =
    serviceFeeBase <= 0
      ? 0
      : Math.round(
          Math.max(SERVICE_FEE_MIN_JOD, Math.min(SERVICE_FEE_MAX_JOD, rawServiceFee)) * 100
        ) / 100;
  const smallOrderFee =
    serviceFeeBase > 0 && serviceFeeBase < SMALL_ORDER_THRESHOLD_JOD ? SMALL_ORDER_FEE_JOD : 0;
  const amountBeforeFees = Math.max(0, totalAfterPoints - walletCreditUsed);
  const deliveryFee =
    effectiveFulfillment === 'delivery' ? deliveryFeeJod(deliveryDropoff?.distanceKm) : 0;
  const amountBeforeTax = Math.max(0, amountBeforeFees + deliveryFee + serviceFee + smallOrderFee);
  const jordanTax = Math.max(
    0,
    Math.round(amountBeforeTax * (JORDAN_TAX_PERCENT / 100) * 100) / 100
  );
  const finalTotal = Math.max(0, amountBeforeTax + jordanTax);
  const needsPaymentChoice = finalTotal > 0;
  const cafeClosed = Boolean(storeHours && !isOpenNow);
  const checkoutDisabled =
    loading || cafeClosed || (needsPaymentChoice && !paymentMethod);

  useEffect(() => {
    if (!user || user.isGuest || user.points < FREE_DRINK_POINTS) {
      if (redeemAllPoints) setRedeemAllPoints(false);
      if (pointsToUse !== 0) setPointsToUse(0);
      return;
    }
    if (redeemAllPoints) {
      if (pointsToUse !== maxRedeemablePoints) {
        setPointsToUse(maxRedeemablePoints);
      }
      return;
    }
    if (pointsToUse > maxRedeemablePoints) {
      setPointsToUse(maxRedeemablePoints);
    }
  }, [redeemAllPoints, user, maxFreeDrinks, pointsToUse]);

  const validateCheckoutPhone = useCallback((): string | null => {
    const normalizedPhone = contactPhone.trim();
    if (!normalizedPhone) {
      return 'Please add your phone number before placing the order.';
    }
    if (normalizedPhone && (normalizedPhone.length < 8 || normalizedPhone.length > 24)) {
      return 'Phone number must be between 8 and 24 characters.';
    }
    return null;
  }, [contactPhone]);

  const persistCheckoutPhone = useCallback(async () => {
    const normalizedPhone = contactPhone.trim();
    const currentPhone = (user?.phone || '').trim();
    if (!user || normalizedPhone === currentPhone) {
      return;
    }
    await axios.put('/api/auth/me', { phone: normalizedPhone || null });
    await updateUser();
  }, [contactPhone, user, updateUser]);

  const buildOrderData = useCallback(
    (): PendingCheckoutOrderData => ({
      cafeId: cafeId || '',
      items: items.map((item) => ({
        menu_item_id: item.menu_item_id,
        quantity: item.quantity,
        note: item.note,
        customizations: item.customizations,
      })),
      pointsUsed: pointsToUseForFreeDrinks,
      walletUsed: walletCreditUsed,
      orderName: orderName.trim() || undefined,
      contactPhone: contactPhone.trim() || undefined,
      promotionId: selectedPromotionId || undefined,
      fulfillmentType: effectiveFulfillment,
      paymentMethod,
      deliveryAddress: deliveryDropoff?.address,
      deliveryLat: deliveryDropoff?.lat,
      deliveryLng: deliveryDropoff?.lng,
      deliveryPlaceId: deliveryDropoff?.placeId || undefined,
    }),
    [
      cafeId,
      items,
      pointsToUseForFreeDrinks,
      walletCreditUsed,
      orderName,
      contactPhone,
      selectedPromotionId,
      effectiveFulfillment,
      paymentMethod,
      deliveryDropoff,
    ]
  );

  const ensureCheckoutSession = useCallback(async (): Promise<boolean> => {
    const storedToken = getCustomerTokenSync();
    if (token || storedToken) return true;
    if (user?.isGuest) {
      try {
        await loginAsGuest();
        return true;
      } catch {
        showToast('Session expired. Please sign in again.', 'error');
        return false;
      }
    }
    showToast('Session expired. Please sign in again.', 'error');
    return false;
  }, [loginAsGuest, showToast, token, user?.isGuest]);

  const openMpgsHostedSession = useCallback(
    async (checkoutData: MpgsHostedCheckoutResponse) => {
      const sessionId = String(checkoutData.sessionId || '').trim();
      const checkoutUrl =
        String(checkoutData.checkoutUrl || '').trim() ||
        (checkoutData.checkoutUrls || []).map((url) => String(url || '').trim()).find(Boolean) ||
        null;
      const scriptUrls = [
        ...((checkoutData.checkoutScriptUrls || []).map((url) => String(url || '').trim())),
        String(checkoutData.checkoutScriptUrl || '').trim(),
        checkoutData.gatewayBaseUrl
          ? `${String(checkoutData.gatewayBaseUrl).replace(/\/+$/, '')}/static/checkout/checkout.min.js`
          : '',
      ].filter((url, index, all) => url && all.indexOf(url) === index);
      const gatewayOrderId =
        String(checkoutData.gatewayOrderId || sessionStorage.getItem('mpgsGatewayOrderId') || '');

      if (checkoutUrl) {
        writeMpgsPayLaunch({ sessionId, scriptUrls, gatewayOrderId, checkoutUrl });
        hostedSessionLaunchInFlight.current = true;
        setCheckoutStatusMessage('Opening secure payment…');
        if (Capacitor.isNativePlatform()) {
          const result = await openHostedCheckoutUrl(checkoutUrl);
          hostedSessionLaunchInFlight.current = false;
          if (result.status === 'cancel' || isCheckoutCancelUrl(result.url)) {
            setCheckoutStatusMessage('');
            setLoading(false);
            showToast('Payment was cancelled.', 'info');
            return;
          }
          navigate(cartPathFromCheckoutExit(result.url), { replace: true });
          return;
        }
        window.location.assign(checkoutUrl);
        return;
      }

      throw new Error('NI did not return a payment page. Please try again.');
    },
    [navigate, showToast]
  );

  const startMpgsHostedCheckout = useCallback(
    async (orderData: PendingCheckoutOrderData) => {
      const randomPart =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
          : `${Date.now()}${Math.floor(Math.random() * 1000000)}`.slice(-12);
      const gatewayOrderId = `QH-${Date.now()}-${randomPart}`.slice(0, 40);
      const basePayload = {
        amount: finalTotal,
        currency: 'JOD',
        gatewayOrderId,
        orderReference: gatewayOrderId,
        returnBaseUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
        description: `Order from ${orderData.orderName || 'QahwaHub'}`,
        paymentMethod: orderData.paymentMethod || paymentMethod || 'visa',
        customer: {
          email: user?.email,
          firstName: user?.name?.split(' ')?.[0] || 'Customer',
          lastName: user?.name?.split(' ')?.slice(1).join(' ') || '',
          phone: orderData.contactPhone || user?.phone || '',
        },
      };

      const createCheckout = (checkoutMode: 'PAYMENT_LINK' | 'WEBSITE') =>
        axios.post(
          '/api/payments/mpgs/create-checkout',
          { ...basePayload, checkoutMode },
          { timeout: 25000 }
        );

      const data: MpgsHostedCheckoutResponse = (await createCheckout('PAYMENT_LINK')).data;
      if (!data?.checkoutUrl && !(data?.checkoutUrls || []).length) {
        throw new Error('NI did not return a payment page. Please try again.');
      }
      const effectiveGatewayOrderId = String(data.gatewayOrderId || gatewayOrderId);
      sessionStorage.setItem('mpgsPendingOrderData', JSON.stringify(orderData));
      sessionStorage.setItem('mpgsSuccessIndicator', String(data.successIndicator || ''));
      sessionStorage.setItem('mpgsGatewayOrderId', effectiveGatewayOrderId);

      await openMpgsHostedSession(data);
    },
    [finalTotal, openMpgsHostedSession, paymentMethod, user?.email, user?.name, user?.phone]
  );

  const completeMpgsReturn = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    if (params.get('mpgs_return') !== '1') return;

    if (hasPendingSubscriptionCheckout()) {
      navigate(`/profile?${params.toString()}`, { replace: true });
      return;
    }

    const gatewayOrderId = params.get('gateway_order_id') || params.get('order.id') || '';
    const resultIndicator = params.get('resultIndicator') || '';

    if (!gatewayOrderId) {
      showToast('Payment callback is missing order details.', 'error');
      return;
    }
    if (mpgsReturnHandled.current === gatewayOrderId) return;
    mpgsReturnHandled.current = gatewayOrderId;

    const pendingOrderDataRaw = sessionStorage.getItem('mpgsPendingOrderData');
    const expectedSuccessIndicator = sessionStorage.getItem('mpgsSuccessIndicator') || '';

    window.history.replaceState({}, '', '/cart');

    if (!pendingOrderDataRaw) {
      showToast('Payment session expired. Please try checkout again.', 'info');
      return;
    }
    if (
      expectedSuccessIndicator &&
      resultIndicator &&
      expectedSuccessIndicator !== resultIndicator
    ) {
      // Some hosted redirect variants can return a different indicator format.
      // Continue with server-side confirmation instead of failing early.
      console.warn('MPGS indicator mismatch; continuing with confirm-order', {
        expectedSuccessIndicator,
        resultIndicator,
        gatewayOrderId,
      });
    }

    setLoading(true);
    try {
      let confirm: any = null;
      let lastConfirmError: any = null;
      for (let i = 0; i < 3; i += 1) {
        try {
          confirm = await axios.post('/api/payments/mpgs/confirm-order', { gatewayOrderId });
          lastConfirmError = null;
          break;
        } catch (confirmErr: any) {
          lastConfirmError = confirmErr;
          const msg = String(confirmErr?.response?.data?.error || '').toLowerCase();
          const retryable = msg.includes('not completed');
          if (!retryable || i === 2) break;
          await new Promise((resolve) => window.setTimeout(resolve, 1400));
        }
      }
      if (!confirm && lastConfirmError) {
        throw lastConfirmError;
      }
      if (!confirm.data?.success) {
        throw new Error(confirm.data?.error || 'Payment not completed');
      }

      const orderData = JSON.parse(pendingOrderDataRaw) as PendingCheckoutOrderData;
      const response = await axios.post('/api/orders', {
        cafe_id: orderData.cafeId,
        items: orderData.items,
        points_used: orderData.pointsUsed || 0,
        wallet_used: orderData.walletUsed || 0,
        name: orderData.orderName,
        payment_method: paymentMethodFromMpgsOrder(
          confirm.data?.order,
          orderData.paymentMethod || 'visa'
        ),
        payment_intent_id: confirm.data?.transactionId || `mpgs:${gatewayOrderId}`,
        ...(orderData.promotionId ? { promotion_id: orderData.promotionId } : {}),
        fulfillment_type: orderData.fulfillmentType || 'pickup',
        ...deliveryFieldsFromPending(orderData),
      });

      clearCart();
      sessionStorage.removeItem('mpgsPendingOrderData');
      sessionStorage.removeItem('mpgsSuccessIndicator');
      sessionStorage.removeItem('mpgsGatewayOrderId');
      sessionStorage.removeItem('mpgsPayLaunch');
      await updateUser();
      hostedSessionLaunchInFlight.current = false;
      setCheckoutStatusMessage('');

      const orderNumberMsg = response.data.order_number ? ` Order #: ${response.data.order_number}` : '';
      showToast(
        `Payment successful!${orderNumberMsg} You earned ${response.data.points_earned} points.`,
        'success',
        5000
      );
      navigate('/orders');
    } catch (err: any) {
      hostedSessionLaunchInFlight.current = false;
      setCheckoutStatusMessage('');
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to verify payment and place your order.';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [location.search, clearCart, navigate, showToast, updateUser]);

  useEffect(() => {
    completeMpgsReturn();
  }, [completeMpgsReturn]);

  const recoverPendingMpgsCheckout = useCallback(async () => {
    if (mpgsRecoveryInFlight.current) return;

    const gatewayOrderId = sessionStorage.getItem('mpgsGatewayOrderId');
    const pendingOrderDataRaw = sessionStorage.getItem('mpgsPendingOrderData');
    if (!gatewayOrderId || !pendingOrderDataRaw) {
      // Ensure checkout button doesn't stay blocked forever if user comes back manually.
      setCheckoutStatusMessage('');
      setLoading(false);
      return;
    }
    if (mpgsReturnHandled.current === gatewayOrderId) return;
    if (!(await ensureCheckoutSession())) {
      sessionStorage.removeItem('mpgsPendingOrderData');
      sessionStorage.removeItem('mpgsSuccessIndicator');
      sessionStorage.removeItem('mpgsGatewayOrderId');
      sessionStorage.removeItem('mpgsPayLaunch');
      return;
    }

    mpgsRecoveryInFlight.current = true;
    setLoading(true);
    try {
      const confirm = await axios.post('/api/payments/mpgs/confirm-order', { gatewayOrderId });
      if (!confirm.data?.success) {
        setLoading(false);
        return;
      }

      mpgsReturnHandled.current = gatewayOrderId;
      const orderData = JSON.parse(pendingOrderDataRaw) as PendingCheckoutOrderData;
      const response = await axios.post('/api/orders', {
        cafe_id: orderData.cafeId,
        items: orderData.items,
        points_used: orderData.pointsUsed || 0,
        wallet_used: orderData.walletUsed || 0,
        name: orderData.orderName,
        payment_method: paymentMethodFromMpgsOrder(
          confirm.data?.order,
          orderData.paymentMethod || 'visa'
        ),
        payment_intent_id: confirm.data?.transactionId || `mpgs:${gatewayOrderId}`,
        ...(orderData.promotionId ? { promotion_id: orderData.promotionId } : {}),
        fulfillment_type: orderData.fulfillmentType || 'pickup',
        ...deliveryFieldsFromPending(orderData),
      });

      clearCart();
      sessionStorage.removeItem('mpgsPendingOrderData');
      sessionStorage.removeItem('mpgsSuccessIndicator');
      sessionStorage.removeItem('mpgsGatewayOrderId');
      sessionStorage.removeItem('mpgsPayLaunch');
      await updateUser();
      hostedSessionLaunchInFlight.current = false;
      setCheckoutStatusMessage('');

      const orderNumberMsg = response.data.order_number ? ` Order #: ${response.data.order_number}` : '';
      showToast(
        `Payment successful!${orderNumberMsg} You earned ${response.data.points_earned} points.`,
        'success',
        5000
      );
      navigate('/orders');
    } catch (err: any) {
      hostedSessionLaunchInFlight.current = false;
      const message = String(err?.response?.data?.error || err?.message || '').toLowerCase();
      // "not completed" is expected if user returned before gateway finalized; keep quiet and allow retry.
      if (message && !message.includes('not completed')) {
        showToast('Could not complete payment recovery. Please open Cart and try again.', 'error');
      }
      setCheckoutStatusMessage('');
      setLoading(false);
    } finally {
      mpgsRecoveryInFlight.current = false;
    }
  }, [clearCart, ensureCheckoutSession, navigate, showToast, updateUser]);

  useEffect(() => {
    void recoverPendingMpgsCheckout();
  }, [recoverPendingMpgsCheckout]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let removed = false;
    let handle: { remove: () => Promise<void> } | undefined;
    void CapacitorApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => {
      if (!isActive) return;
      void recoverPendingMpgsCheckout();
    }).then((h: { remove: () => Promise<void> }) => {
      if (removed) {
        void h.remove();
      } else {
        handle = h;
      }
    });
    return () => {
      removed = true;
      if (handle) {
        void handle.remove();
      }
    };
  }, [recoverPendingMpgsCheckout]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mpgs_cancel') === '1') {
      if (hasPendingSubscriptionCheckout()) {
        navigate(`/profile?${params.toString()}`, { replace: true });
        return;
      }
      window.history.replaceState({}, '', '/cart');
      hostedSessionLaunchInFlight.current = false;
      setCheckoutStatusMessage('');
      setLoading(false);
      showToast('Payment was cancelled.', 'info');
    }
  }, [location.search, navigate, showToast]);

  const handleCheckout = async () => {
    if (!cafeId || items.length === 0) {
      setError('Cart is empty');
      showToast('Your cart is empty!', 'error');
      return;
    }

    if (storeHours && !isCafeOpenNow(storeHours)) {
      const message = getCafeClosedMessage(storeHours);
      setError(message);
      showToast(message, 'error');
      return;
    }

    trackFunnelEvent('checkout_started', cafeId);

    // Payment method is only required if the order has a cost (finalTotal > 0)
    if (finalTotal > 0 && !paymentMethod) {
      setError('Please select a payment method');
      showToast('Please select a payment method', 'error');
      return;
    }

    const phoneValidationError = validateCheckoutPhone();
    if (phoneValidationError) {
      setError(phoneValidationError);
      showToast(phoneValidationError, 'error');
      return;
    }

    if (effectiveFulfillment === 'delivery') {
      if (!deliveryDropoff) {
        const message = 'Drop your delivery pin on the map so Google can measure the distance.';
        setError(message);
        showToast(message, 'error');
        return;
      }
      if (deliveryDropoff.distanceKm == null) {
        const message =
          'Wait for the Google driving distance, or move the pin if Google could not find a route.';
        setError(message);
        showToast(message, 'error');
        return;
      }
    }

    if (!(await ensureCheckoutSession())) {
      setLoading(false);
      return;
    }

    try {
      await persistCheckoutPhone();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to save your phone number.';
      setError(message);
      showToast(message, 'error');
      return;
    }

    const orderData = buildOrderData();

    if (finalTotal > 0 && isHostedCheckoutMethod(paymentMethod)) {
      setLoading(true);
      try {
        await startMpgsHostedCheckout(orderData);
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to start secure payment.';
        setError(message);
        setCheckoutStatusMessage('');
        showToast(message, 'error');
        setLoading(false);
      }
      return;
    }

    await placeOrder('cash', orderData);
  };

  const placeOrder = useCallback(async (
    method: string,
    orderDataOverride?: PendingCheckoutOrderData,
    paymentIntentId?: string
  ) => {
    const orderData = orderDataOverride || buildOrderData();
    if (!orderData.cafeId || orderData.items.length === 0) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/orders', {
        cafe_id: orderData.cafeId,
        items: orderData.items,
        points_used: orderData.pointsUsed,
        wallet_used: orderData.walletUsed,
        name: orderData.orderName,
        payment_method: finalTotal > 0 ? method : 'points',
        ...(orderData.promotionId ? { promotion_id: orderData.promotionId } : {}),
        fulfillment_type: orderData.fulfillmentType || 'pickup',
        ...deliveryFieldsFromPending(orderData),
        ...(paymentIntentId && { payment_intent_id: paymentIntentId }),
      });

      clearCart();
      await updateUser();
      console.log('Order response:', response.data);
      const freeDrinksMsg = response.data.free_drinks > 0 ? ` You redeemed ${response.data.free_drinks} free drink(s)!` : '';
      const walletMsg = response.data.wallet_used > 0 ? ` Wallet used: ${formatCurrency(response.data.wallet_used)}.` : '';
      const orderNumberMsg = response.data.order_number ? ` Order #: ${response.data.order_number}` : '';
      showToast(`Order placed successfully!${orderNumberMsg} You earned ${response.data.points_earned} points.${freeDrinksMsg}${walletMsg}`, 'success', 5000);
      navigate('/orders');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to place order');
      showToast(err.response?.data?.error || 'Failed to place order', 'error');
    } finally {
      setLoading(false);
    }
  }, [buildOrderData, clearCart, finalTotal, navigate, showToast, updateUser]);

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-4 text-amber-800">Your Cart</h1>
        <EmptyState
          icon="🛒"
          title="Your cart is empty"
          message="Start adding items from our cafes to your cart!"
          actionLabel="Browse Cafes"
          actionPath="/cafes"
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 pb-[calc(9rem+env(safe-area-inset-bottom,0px))] lg:pb-8">
      <h1 className="text-3xl font-bold mb-2 text-amber-800">Your Cart</h1>
      <p className="text-sm text-gray-600 mb-4">
        Need help before you order? Email{' '}
        <a className="font-semibold text-amber-800" href="mailto:support@qahwahub.com">
          support@qahwahub.com
        </a>
      </p>
      {cafeName && (
        <p className="text-base font-semibold text-amber-900 mb-6">📍 {cafeName}</p>
      )}
      
      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      {cafeClosed && storeHours && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          <p className="font-semibold">This café is closed for orders</p>
          <p className="text-sm mt-1">{getCafeClosedMessage(storeHours)}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 sm:p-6">
            {items.map((item, index) => (
              <div
                key={`${item.menu_item_id}-${index}`}
                className="flex flex-row items-start gap-3 border-b border-gray-100 pb-5 mb-5 last:mb-0 last:border-b-0"
              >
                {/* Wide column: one logical line each (ellipsis if overflow) */}
                <div className="min-w-0 flex-1 space-y-1 pr-1">
                  <h3
                    className="font-bold text-base text-gray-900 leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
                    title={item.name}
                  >
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatCurrency(item.price)} each
                  </p>
                  {item.customizations && (
                    <div className="pt-1 space-y-1">
                      {Object.entries(item.customizations).map(([key, value]) => {
                        const label =
                          key === 'size'
                            ? 'Cup size'
                            : key === 'milk'
                              ? 'Milk'
                              : key === 'shots'
                                ? 'Shots'
                                : key === 'sweetness'
                                  ? 'Sweetness'
                                  : key === 'ice'
                                    ? 'Ice'
                                    : key === 'topping'
                                      ? 'Topping'
                                      : key;
                        const display = String(value).replace(/-/g, ' ');
                        const line = `${label}: ${display}`;
                        return (
                          <p
                            key={key}
                            className="text-xs text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis"
                            title={line}
                          >
                            <span className="font-semibold text-gray-800">{label}:</span>{' '}
                            <span className="font-normal text-gray-600">{display}</span>
                          </p>
                        );
                      })}
                    </div>
                  )}
                  {item.note && (
                    <p
                      className="text-xs text-amber-800 pt-1 whitespace-nowrap overflow-hidden text-ellipsis"
                      title={`Note: ${item.note}`}
                    >
                      Note: {item.note}
                    </p>
                  )}
                </div>

                {/* Narrow column: qty, line total, remove — stacked */}
                <div className="flex shrink-0 w-[5.5rem] flex-col items-center gap-2">
                  <div className="flex items-center justify-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1)}
                      className="bg-gray-200 hover:bg-gray-300 active:bg-gray-400 w-9 h-9 rounded-md text-base font-semibold leading-none"
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)' }}
                    >
                      −
                    </button>
                    <span className="w-7 text-center text-sm font-semibold tabular-nums">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1)}
                      className="bg-gray-200 hover:bg-gray-300 active:bg-gray-400 w-9 h-9 rounded-md text-base font-semibold leading-none"
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)' }}
                    >
                      +
                    </button>
                  </div>
                  <span className="text-center text-xs font-bold text-amber-900 tabular-nums leading-tight px-0.5 w-full whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatCurrency(item.price * item.quantity)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(item.menu_item_id)}
                    className="text-[11px] font-semibold text-red-600 hover:text-red-800 py-1 px-1 rounded-md hover:bg-red-50 text-center w-full"
                    style={{ touchAction: 'manipulation' }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 sm:p-6 lg:sticky lg:top-4">
            <h2 className="text-xl font-bold mb-1">Order Summary</h2>
            {cafeName && (
              <p className="text-sm font-semibold text-amber-900 mb-4">📍 {cafeName}</p>
            )}
            <div className="space-y-4">
              {checkoutStatusMessage && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                  {checkoutStatusMessage}
                </div>
              )}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                  Charges
                </h3>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                  {promotionDiscount > 0 && (
                    <div className="flex justify-between text-emerald-800 font-medium">
                      <span>Promotion discount:</span>
                      <span>-{formatCurrency(promotionDiscount)}</span>
                    </div>
                  )}
                  {effectiveFulfillment === 'delivery' && (
                    <div className="flex justify-between text-gray-700">
                      <span>
                        Delivery fee
                        {deliveryDropoff?.distanceKm != null
                          ? ` (${formatDrivingKm(deliveryDropoff.distanceKm)})`
                          : ''}
                        :
                      </span>
                      <span>{formatCurrency(deliveryFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-700">
                    <span>Service fee ({serviceFeePercent}%):</span>
                    <span>{formatCurrency(serviceFee)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Small order fee:</span>
                    <span>{formatCurrency(smallOrderFee)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Tax (Jordanian tax {JORDAN_TAX_PERCENT}%):</span>
                    <span>{formatCurrency(jordanTax)}</span>
                  </div>
                </div>
              </div>

              {user?.isGuest && (
                <div className="p-2.5 bg-blue-50 rounded border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Create an account</strong> to earn points and save your order history.
                  </p>
                </div>
              )}

              {user && !user.isGuest && (
                <div>
                  {user.points >= FREE_DRINK_POINTS ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50/30 p-2.5">
                      <label className="block text-sm font-semibold text-amber-900 mb-1.5">
                        Redeem points
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={maxRedeemablePoints}
                        step={FREE_DRINK_POINTS}
                        value={pointsToUse}
                        disabled={redeemAllPoints}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          const rounded = Math.floor(val / FREE_DRINK_POINTS) * FREE_DRINK_POINTS;
                          setPointsToUse(Math.min(rounded, maxRedeemablePoints));
                          if (redeemAllPoints) setRedeemAllPoints(false);
                        }}
                        className="w-full px-2.5 py-1.5 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-amber-100/60 disabled:text-amber-900/70"
                        placeholder="Points to redeem"
                      />
                      <label className="mt-1.5 inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={redeemAllPoints}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setRedeemAllPoints(checked);
                            if (!checked) setPointsToUse(0);
                          }}
                          className="h-4 w-4 accent-amber-700"
                        />
                        Redeem all points ({maxRedeemablePoints})
                      </label>
                    </div>
                  ) : (
                    <div className="p-2.5 bg-gray-50 rounded border border-gray-200">
                      <p className="text-xs text-gray-600">
                        You have {user.points.toString().replace(/\.0+$/, '')} points.
                        <span className="font-semibold text-amber-700"> Earn {FREE_DRINK_POINTS - (user.points || 0)} more points</span> to redeem.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {freeDrinks > 0 && (
                <div className="p-2.5 bg-green-50 rounded border border-green-300">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-green-800 text-sm">Free coffee applied</span>
                    <span className="font-bold text-green-700 text-sm">-{formatCurrency(freeDrinkDiscount)}</span>
                  </div>
                  <p className="text-xs text-green-700">
                    Redeemed {pointsToUse} points for {freeDrinks} free coffee{freeDrinks > 1 ? 's' : ''}.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Order type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {FULFILLMENT_OPTIONS.map((opt) => {
                  const selected = fulfillmentType === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setFulfillmentType(opt.id)}
                      className={`rounded-lg border-2 px-2.5 py-2 text-left transition active:scale-[0.99] ${
                        selected
                          ? 'border-amber-700 bg-amber-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/40'
                      }`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <span className="block text-[13px] font-bold text-gray-900">{opt.label}</span>
                      <span className="block text-[11px] text-gray-600 mt-0.5">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
              {effectiveFulfillment === 'delivery' && cafeId && (
                <DeliveryLocationPicker
                  cafeId={cafeId}
                  cafeOrigin={cafeOrigin}
                  value={deliveryDropoff}
                  onChange={setDeliveryDropoff}
                />
              )}
            </div>

            {needsPaymentChoice && (
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <label
                    className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-amber-50/80 transition active:scale-[0.99]"
                    style={{ borderColor: paymentMethod === 'visa' ? '#b45309' : '#e5e7eb' }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="visa"
                      checked={paymentMethod === 'visa'}
                      onChange={(e) => setPaymentMethod(e.target.value as CheckoutPaymentMethod)}
                      className="mr-3 h-4 w-4 shrink-0 accent-amber-700"
                    />
                    <div className="min-w-0">
                      <span className="font-semibold">Card</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Visa, Mastercard, Google Pay, or Apple Pay on the next screen
                      </p>
                    </div>
                  </label>
                  <label
                    className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-amber-50/80 transition active:scale-[0.99]"
                    style={{ borderColor: paymentMethod === 'cash' ? '#b45309' : '#e5e7eb' }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={(e) => setPaymentMethod(e.target.value as CheckoutPaymentMethod)}
                      className="mr-3 h-4 w-4 shrink-0 accent-amber-700"
                    />
                    <div className="min-w-0">
                      <span className="font-semibold">Cash</span>
                      <p className="text-xs text-gray-500 mt-0.5">Pay at the café</p>
                    </div>
                  </label>
                  <p className="text-xs text-gray-500 pl-1">
                    Card opens NI’s secure page. Choose card, Google Pay, or Apple Pay there.
                    Apple Pay is iPhone Safari only.
                  </p>
                  {user && !user.isGuest && availableWallet > 0 && (
                    <div
                      className="p-3 border-2 rounded-lg transition"
                      style={{ borderColor: walletEnabled ? '#b45309' : '#e5e7eb' }}
                    >
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={walletEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setWalletEnabled(checked);
                            if (!checked) {
                              setWalletToUse(0);
                              return;
                            }
                            setWalletToUse(Math.min(availableWallet, totalAfterPromo));
                          }}
                          className="mr-3 h-4 w-4 shrink-0 accent-amber-700"
                        />
                        <div className="flex items-center">
                          <span className="text-2xl mr-2">🤎</span>
                          <span className="font-semibold text-amber-900">
                            Qahwahub credit
                          </span>
                        </div>
                        <div className="ml-auto text-sm font-semibold text-amber-700">
                          {formatCurrency(availableWallet)} available
                        </div>
                      </label>
                      {walletEnabled && (
                        <div className="mt-3">
                          <input
                            type="number"
                            min="0"
                            max={Math.min(availableWallet, totalAfterPoints)}
                            step="0.1"
                            value={walletToUse}
                            onChange={(e) => {
                              const raw = Number(e.target.value || 0);
                              if (!Number.isFinite(raw)) {
                                setWalletToUse(0);
                                return;
                              }
                              setWalletToUse(Math.max(0, Math.min(raw, availableWallet, totalAfterPoints)));
                            }}
                            className="w-full px-3 py-2 border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="0.00"
                          />
                          {walletCreditUsed > 0 && (
                            <p className="text-xs text-amber-800 mt-2">
                              Wallet credit applied: -{formatCurrency(walletCreditUsed)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {finalTotal === 0 && (
              <div className="mt-4 p-3 bg-green-50 border-2 border-green-300 rounded">
                <p className="text-sm text-green-800 font-semibold">
                  🎉 Your order is completely free! No payment method needed.
                </p>
              </div>
            )}

            <div className="flex justify-between font-bold text-lg pt-2 mt-4 border-t">
              <span>Total:</span>
              <span>{formatCurrency(finalTotal)}</span>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Contact phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="e.g., +9627XXXXXXXX"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                maxLength={24}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                If your phone is saved in Profile → Account Information, it will auto-fill here.
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Order Name (Optional)
              </label>
              <input
                type="text"
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
                placeholder={
                  fulfillmentType === 'delivery'
                    ? 'e.g., Floor, apartment, or extra delivery notes'
                    : 'e.g., Pickup for John'
                }
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                Add a name or note to help identify this order
              </p>
            </div>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutDisabled}
              className="lg:hidden w-full bg-gradient-to-r from-amber-700 to-amber-600 active:from-amber-800 active:to-amber-700 text-white py-3.5 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-5 font-semibold shadow-md"
              style={{
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                pointerEvents: checkoutDisabled ? 'none' : 'auto',
              }}
            >
              {loading ? 'Processing…' : 'Place Order'}
            </button>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutDisabled}
              className="hidden lg:block w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-800 hover:to-amber-700 text-white py-3.5 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-4 font-semibold shadow-md"
              style={{
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                pointerEvents: checkoutDisabled ? 'none' : 'auto',
              }}
            >
              {loading ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile: must sit above BottomNavigation (z-50), not behind it */}
      <div
        className="lg:hidden fixed left-0 right-0 z-[55] border-t border-amber-200/80 bg-[#fff8f0] dark:bg-[#1a1410] backdrop-blur-md px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.12)]"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="container mx-auto flex items-center gap-3 max-w-lg">
          <div className="min-w-0 shrink">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Total</p>
            <p className="text-lg font-bold text-amber-900 dark:text-amber-200 truncate">
              {formatCurrency(finalTotal)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCheckout}
            disabled={checkoutDisabled}
            className="flex-1 min-w-0 bg-gradient-to-r from-amber-700 to-amber-600 active:from-amber-800 active:to-amber-700 text-white py-3.5 rounded-xl font-semibold shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
              pointerEvents: checkoutDisabled ? 'none' : 'auto',
            }}
          >
            {loading ? 'Processing…' : 'Place Order'}
          </button>
        </div>
      </div>

    </div>
  );
}
